import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { LOGIN_MUTATION } from '../graphql/mutations/auth';
import type { User } from '../types/auth';

// Hook personnalisé pour gérer l'authentification
export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Charger l'utilisateur depuis le localStorage au démarrage
  // Connexion classique = token + user ; Google OAuth = user seul (auth par cookie)
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        setUser(parsed);
        setToken(storedToken || 'cookie'); // 'cookie' = session Google OAuth (pas de token en localStorage)
      } catch (error) {
        console.error('Erreur lors du chargement des données utilisateur:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    } else if (storedToken) {
      setToken(storedToken);
    }

    setLoading(false);
  }, []);

  // Fonction de connexion
  const login = async (email: string, password: string) => {
    try {
      // Pour le développement, simuler une connexion réussie
      const userData = {
        id: 1,
        email,
        role: 'AGENT',
        agencyName: 'Agence Test',
        agency: {
          name: 'Agence Test',
          logo: null
        }
      };
      
      const fakeToken = 'fake-token-' + Math.random().toString(36).substring(2);
      
      // Stocker les données dans le localStorage
      localStorage.setItem('token', fakeToken);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Mettre à jour l'état
      setToken(fakeToken);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      console.error('Erreur de connexion:', error);
      return { success: false, error };
    }
  };

  // Fonction de déconnexion
  const logout = () => {
    // Supprimer les données du localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Réinitialiser l'état
    setToken(null);
    setUser(null);
  };

  return {
    user,
    token,
    loading,
    isAuthenticated: !!token,
    login,
    logout
  };
}

// Fonctions utilitaires
export const isAdmin = () => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  return user?.role === 'ADMIN';
};

export const isAgent = () => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  return user?.role === 'AGENT';
};

export const hasAgency = () => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  return !!user?.agencyName;
};

export const getAgencyName = () => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  return user?.agencyName;
};

export const isSuperAdmin = () => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  return user?.role === 'SUPERADMIN';
}; 